import { create } from 'zustand';

/**
 * Store global (placeholder F0-P2). En P4 alojará la sesión del participante,
 * la superficie activa (Bolsillo / Cabina / Pantalla) y el estado de la
 * barra XIA. Por ahora tema + modo sobrio, ambos con default institucional.
 *
 * TEMA: cada superficie tiene su default (Bolsillo claro; Cabina/Pantalla
 * navy), pero la persona puede invertirlo con el botón claro/oscuro (pedido
 * del owner). La preferencia se guarda en localStorage y le gana al default
 * de la superficie — salvo en la Pantalla, que proyecta a 6 metros y queda
 * siempre en navy (ver useSurfaceTheme).
 */
export type Surface = 'bolsillo' | 'cabina' | 'pantalla';
export type Theme = 'light' | 'dark';

const CLAVE_TEMA = 'agora.tema';

function leerTemaGuardado(): Theme | null {
  try {
    const v = window.localStorage.getItem(CLAVE_TEMA);
    return v === 'light' || v === 'dark' ? v : null;
  } catch {
    return null; // modo privado sin storage: no hay preferencia
  }
}

function guardarTema(theme: Theme | null): void {
  try {
    if (theme === null) window.localStorage.removeItem(CLAVE_TEMA);
    else window.localStorage.setItem(CLAVE_TEMA, theme);
  } catch {
    /* sin storage la preferencia dura lo que dura la pestaña */
  }
}

interface AppState {
  /** Superficie activa; determina densidad y chrome. */
  surface: Surface;
  /** Tema visual EFECTIVO (ya resuelto: preferencia u override de superficie). */
  theme: Theme;
  /** Preferencia manual de la persona; null = respetar el default de la superficie. */
  temaElegido: Theme | null;
  /** Modo sobrio del vocabulario gamificado (default institucional true). */
  modoSobrio: boolean;
  setSurface: (surface: Surface) => void;
  setTheme: (theme: Theme) => void;
  /** Invierte el tema actual y lo persiste como preferencia. */
  toggleTheme: () => void;
  setModoSobrio: (value: boolean) => void;
}

export const useAppStore = create<AppState>((set) => ({
  surface: 'bolsillo',
  theme: 'light',
  temaElegido: leerTemaGuardado(),
  modoSobrio: true,
  setSurface: (surface) => set({ surface }),
  setTheme: (theme) => set({ theme }),
  toggleTheme: () =>
    set((state) => {
      const nuevo: Theme = state.theme === 'light' ? 'dark' : 'light';
      guardarTema(nuevo);
      // Aplicar de inmediato: useSurfaceTheme re-sincroniza en la próxima
      // navegación, pero el clic tiene que verse YA.
      document.documentElement.setAttribute('data-theme', nuevo);
      return { theme: nuevo, temaElegido: nuevo };
    }),
  setModoSobrio: (modoSobrio) => set({ modoSobrio }),
}));
